import { useState } from "react";
import { classNames } from "../../UI/Utils/classNames.ts";
import { TableColumnHeader } from "../../UI/Table/TableColumnHeader.tsx";
import { formatCount } from "./salesFormat.ts";
import { EmptyStatistics } from "./StatisticsCard.tsx";
import { SalesDetailsColumn, tableColumnsOf } from "./salesDetailsColumns.tsx";
import {
  isActiveColumnFilter,
  SalesColumnFilters,
  SalesColumnFilterValue,
} from "./salesColumnFilters.ts";
import { ColumnFilterPanel } from "./ColumnFilterPanel.tsx";

const ROWS_PER_PAGE = 25;

export function SalesDetailsTable<Row>({
  rows,
  unfilteredRows,
  columns,
  filters,
  onFilter,
  rowKeyOf,
  noRowsMessage,
  noMatchingRowsMessage,
}: {
  rows: Row[];
  unfilteredRows: Row[];
  columns: SalesDetailsColumn<Row>[];
  filters: SalesColumnFilters;
  onFilter: (header: string, value: SalesColumnFilterValue) => void;
  rowKeyOf: (row: Row) => string;
  noRowsMessage: string;
  noMatchingRowsMessage: string;
}) {
  const [shownRowCount, setShownRowCount] = useState(ROWS_PER_PAGE);
  if (unfilteredRows.length === 0)
    return <EmptyStatistics>{noRowsMessage}</EmptyStatistics>;

  const shownColumns = tableColumnsOf(columns);
  const hiddenRowCount = Math.max(0, rows.length - shownRowCount);

  return (
    <div className="flex flex-col gap-2">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-gray-500 uppercase border-b border-gray-200">
              {shownColumns.map((column) => {
                const { filter } = column;
                return (
                  <th
                    key={column.header}
                    className={classNames(
                      "py-2 pr-3 font-medium whitespace-nowrap",
                      column.isNumeric ? "text-right" : undefined,
                    )}
                  >
                    <TableColumnHeader
                      label={column.header}
                      isFiltered={isActiveColumnFilter(filters[column.header])}
                      isPanelWide={filter?.kind === "values"}
                      renderFilter={
                        filter
                          ? (close) => (
                              <ColumnFilterPanel
                                filter={filter}
                                unfilteredRows={unfilteredRows}
                                value={filters[column.header]}
                                onApply={(value) =>
                                  onFilter(column.header, value)
                                }
                                close={close}
                              />
                            )
                          : undefined
                      }
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={shownColumns.length}
                  className="py-8 text-center text-sm text-gray-400"
                >
                  {noMatchingRowsMessage}
                </td>
              </tr>
            )}
            {rows.slice(0, shownRowCount).map((row) => (
              <tr
                key={rowKeyOf(row)}
                className="border-b border-gray-100 text-gray-700"
              >
                {shownColumns.map((column) => (
                  <td
                    key={column.header}
                    className={classNames(
                      "py-1.5 pr-3 align-top",
                      column.isNumeric
                        ? "text-right whitespace-nowrap"
                        : undefined,
                    )}
                  >
                    {column.cellOf ? column.cellOf(row) : column.valueOf(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
        <span>
          {formatCount(rows.length - hiddenRowCount)} di{" "}
          {formatCount(rows.length)}
        </span>
        {hiddenRowCount > 0 && (
          <button
            type="button"
            onClick={() =>
              setShownRowCount((rowCount) => rowCount + ROWS_PER_PAGE)
            }
            className="text-blue-600 hover:underline"
          >
            Mostra altri {formatCount(Math.min(ROWS_PER_PAGE, hiddenRowCount))}
          </button>
        )}
      </div>
    </div>
  );
}
